'use client';

import { Link } from '@/lib/app-router';
import { cn, focusRing } from '@/lib/styleUtils';
import type { AbmLinkProps } from './AbmLink.types';

/**
 * A link on the ABM marketing site.
 *
 * ## Invariants:
 * - External links render a plain anchor: the app-router `Link` prefixes every href with the
 *   company base path, which would turn `https://abm.com` into a broken relative path.
 */
export function AbmLink({
  href,
  external = false,
  newTabLabel,
  className,
  children,
  onClick,
  'data-cy': dataCy,
}: AbmLinkProps) {
  const classes = cn(focusRing, className);

  if (external) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={classes}
        onClick={onClick}
        data-cy={dataCy}
      >
        {children}
        {newTabLabel && <span className="tw:sr-only"> {newTabLabel}</span>}
      </a>
    );
  }

  return (
    <Link href={href} className={classes} onClick={onClick} data-cy={dataCy}>
      {children}
    </Link>
  );
}
